import { Auth } from 'aws-amplify';
import { FEDERATED_TOKENS_INITIAL, ID_TOKEN } from '@/data/cognito';
import jwt, { JwtPayload } from 'jsonwebtoken';
import jwkToPem from 'jwk-to-pem';
import apiConfig from '@/configs/api';

import Stores from '@/stores';
import EventBus from './EventBus';
import Debug from '@/utils/Debug';
import CaptureException from '@/services/CaptureException';
import { jwtDecode } from 'jwt-decode';
import Operator from './Operator';

class Login {
  /**
   * login user by email and password via amplify/cognito
   * @param {string} email
   * @param {string} password
   * @return {Promise}
   */
  public async login(email: string, password: string): Promise<void> {
    let loginResponse = null;

    try {
      loginResponse = await Auth.signIn(email.toLowerCase(), password);
    } catch (error: any) {
      if (error) {
        EventBus.emit(EventBus.keys.LOGIN_ERROR, error.message);
      }

      throw error;
    }

    // TODO: reset password is not implemented, yet
    const newPassword: string = '';
    if (typeof loginResponse.challengeName !== 'undefined') {
      if (loginResponse.challengeName === 'NEW_PASSWORD_REQUIRED') {
        if (newPassword !== '') {
          try {
            await Auth.completeNewPassword(
              loginResponse, // the Cognito User Object
              newPassword, // the new password
              { email: email.toLowerCase() },
            );
          } catch (error: any) {
            Stores.userData.passwordChallenge = false;

            if (error) {
              Stores.userData.loginError = error.code || error;
            }

            throw error;
          }

          await this.fetchUser();
        } else {
          Stores.userData.passwordChallenge = true;
        }
      }
    }
    //
    else {
      await this.fetchUser();
    }
  }

  public async verifyCandidateToken(queryParamsToken: string): Promise<string | JwtPayload> {
    const candidateJwksResponse = await fetch(apiConfig.amplify.candidateJwksUrl);
    const candidateJwks = await candidateJwksResponse.json();

    const decodedHeader = jwt.decode(queryParamsToken, { complete: true });
    if (!decodedHeader) {
      throw new Error('Invalid token: Unable to decode header');
    }

    const keyId = decodedHeader.header.kid;
    const key = candidateJwks.keys.find((k: any) => k.kid === keyId);
    if (!key) {
      throw new Error('Public key not found in JWKS');
    }

    // Convert JWK to PEM format
    const publicKey = jwkToPem(key);

    // Verify the token
    return jwt.verify(queryParamsToken, publicKey, {
      algorithms: ['RS256'],
    });
  }

  public async loginByCandidateFederation(token: string): Promise<void> {
    try {
      const authResult = await Operator.provideCognitoAccess(token);

      localStorage.setItem(FEDERATED_TOKENS_INITIAL, JSON.stringify(JSON.parse(authResult).AuthenticationResult));
    } catch (error: any) {
      console.error('Federated Sign-In Error:', error);
      EventBus.emit(EventBus.keys.LOGIN_ERROR, error.message);
      throw error;
    }
  }

  public async fetchFederatedUser(tokens: any): Promise<void> {
    let tokensToUse = tokens;
    if (typeof tokens === 'string') {
      tokensToUse = JSON.parse(tokens);
    }
    if (!tokensToUse) {
      return;
    }
    const user = await Auth.currentAuthenticatedUser();

    const decodedToken = jwtDecode(tokensToUse.id_token);
    if (!decodedToken || !decodedToken.exp) {
      return;
    }

    const expires = decodedToken.exp - Math.floor(new Date().getTime() / 1000);

    Stores.userData.userType = user['attributes']['custom:userType'];

    localStorage.setItem(ID_TOKEN, tokensToUse.id_token);
    Stores.userData.idToken = tokensToUse.id_token;

    Debug.toConsole(`[Federated User]: Token expires in ${expires} seconds`);

    setTimeout(async () => {
      Debug.toConsole('Renewing Token');
      await this.fetchFederatedUser(tokensToUse);
    }, expires * 1000);

    Stores.userData.attributes = {
      type: user['attributes']['custom:userType'],
      email: user['attributes']['email'],
      email_verified: user['attributes']['email_verified'],
      sub: user['attributes']['sub'],
    };
    Stores.userData.signIn();
  }

  /**
   * get user attributes and information out of the current instance of amplify Auth
   *
   * @return {Promise}
   */
  public async fetchUser(): Promise<void> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const info = await Auth.currentUserInfo();

      if (!user || !info) {
        return;
      }

      Stores.userData.userType = info['attributes']['custom:userType'];

      const expires = user.getSignInUserSession().getIdToken().payload.exp - Math.floor(new Date().getTime() / 1000);
      const token = user.getSignInUserSession().getIdToken().jwtToken;

      localStorage.setItem(ID_TOKEN, token);
      Stores.userData.idToken = token;

      Debug.toConsole(`Token expires in ${expires} seconds`);

      setTimeout(async () => {
        Debug.toConsole('Renewing Token');
        await this.fetchUser();
      }, expires * 1000);

      Stores.userData.attributes = {
        type: user['attributes']['custom:userType'],
        email: user['attributes']['email'],
        email_verified: user['attributes']['email_verified'],
        sub: user['attributes']['sub'],
      };
      Stores.userData.signIn();
    } catch (err) {
      CaptureException.send(err);
    }
  }
}

export default new Login();
